"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import { getCanonicalNextMintNumber, formatFullDateTime, getNextMintStart } from "@/components/meme-calendar/meme-calendar.helpers";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import Image from "next/image";
import Link from "next/link";
import ArtistPill from "./ArtistPill";
import LatestDropNextMintSubscribe from "./LatestDropNextMintSubscribe";
import NowMintingStatsItem from "./NowMintingStatsItem";
interface LatestDropNextMintSectionProps {
  readonly drop: ApiDrop;
}
const formatDropTimestamp = (timestamp: number): string | null => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const dateLabel = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric"
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
  return `${dateLabel} · ${timeLabel}`;
};
export default function LatestDropNextMintSection(t0) {
  const $ = _c(61);
  const {
    drop
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const isHtml = media?.mime_type === "text/html";
  let t1;
  if ($[0] !== drop.metadata || $[1] !== hasTouchScreen || $[2] !== isHtml) {
    t1 = hasTouchScreen && isHtml ? getDropPreviewImageUrl(drop.metadata) ?? undefined : undefined;
    $[0] = drop.metadata;
    $[1] = hasTouchScreen;
    $[2] = isHtml;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const htmlPreviewImageUrl = t1;
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  const authorHandle = author.handle ?? author.primary_address;
  const authorName = author.handle ?? "Anonymous";
  let t2;
  if ($[4] !== drop.created_at) {
    t2 = formatDropTimestamp(drop.created_at);
    $[4] = drop.created_at;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const submittedAt = t2;
  const description = drop.metadata.find(_temp2)?.data_value ?? null;
  let nextMintCardNumber;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    const now = new Date();
    const nextMintStart = getNextMintStart(now);
    nextMintCardNumber = getCanonicalNextMintNumber(now);
    t3 = formatFullDateTime(nextMintStart, "local");
    $[6] = nextMintCardNumber;
    $[7] = t3;
  } else {
    nextMintCardNumber = $[6];
    t3 = $[7];
  }
  const nextMintDateTime = t3;
  const nextMintLabel = `Card #${nextMintCardNumber} - ${nextMintDateTime}`;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="tw-mb-3 tw-block tw-text-xl tw-font-semibold tw-tracking-tight tw-text-iron-100 md:tw-mb-4 md:tw-text-2xl">Next Drop</span>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== hasTouchScreen || $[10] !== htmlPreviewImageUrl || $[11] !== media) {
    t5 = <div className="tw-p-0 lg:tw-col-span-6 xl:tw-col-span-5"><div className="tw-relative tw-flex tw-h-[clamp(360px,65vw,640px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-black/50"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">{media ? <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageObjectPosition="center" imageScale={ImageScale.AUTOx600} disableAutoPlay={hasTouchScreen} disableModal={hasTouchScreen} htmlIframeContainerClassName="tw-w-full" htmlPreviewImageUrl={htmlPreviewImageUrl} /> : <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-black/40"><span className="tw-text-sm tw-text-white/40">No image</span></div>}</div></div></div>;
    $[9] = hasTouchScreen;
    $[10] = htmlPreviewImageUrl;
    $[11] = media;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2"><div className="tw-flex tw-items-center tw-gap-2"><span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" /><span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide tw-text-emerald-400">NEXT MINT</span></div></div>;
    t7 = <span className="tw-mt-1 tw-font-mono tw-text-xs tw-text-white/50">{nextMintLabel}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  const t8 = `/waves?wave=${drop.wave.id}&drop=${drop.id}`;
  let t9;
  if ($[15] !== t8 || $[16] !== title) {
    t9 = <Link href={t8} className="tw-mt-3 tw-text-xl tw-font-semibold tw-leading-tight tw-text-iron-50 tw-no-underline tw-transition-colors tw-duration-300 desktop-hover:hover:tw-text-iron-200 sm:tw-text-2xl md:tw-text-3xl">{title}</Link>;
    $[15] = t8;
    $[16] = title;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== description) {
    t10 = description && <p className="tw-mt-3 tw-line-clamp-2 tw-text-sm tw-leading-relaxed tw-text-iron-300">{description}</p>;
    $[18] = description;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== drop.id || $[21] !== media) {
    t11 = media?.mime_type && <MediaTypeBadge mimeType={media.mime_type} dropId={drop.id} size="sm" iconClassName="tw-size-[26px]" />;
    $[20] = drop.id;
    $[21] = media;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = authorHandle ? `/${authorHandle}` : undefined;
  const t13 = author.handle ?? undefined;
  let t14;
  if ($[23] !== author.pfp || $[24] !== authorName || $[25] !== t12 || $[26] !== t13) {
    t14 = <ArtistPill pfp={author.pfp} label={authorName} href={t12} profileHandle={t13} />;
    $[23] = author.pfp;
    $[24] = authorName;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t14) {
    t15 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{t11}{t14}</div>;
    $[28] = t11;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <LatestDropNextMintSubscribe />;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t10 || $[33] !== t15 || $[34] !== t9) {
    t17 = <div className="tw-flex tw-flex-col">{t6}{t7}{t9}{t10}{t15}{t16}</div>;
    $[32] = t10;
    $[33] = t15;
    $[34] = t9;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = `/waves/${drop.wave.id}`;
  let t19;
  if ($[36] !== drop.wave.name || $[37] !== drop.wave.picture) {
    t19 = drop.wave.picture && <Image src={getScaledImageUri(drop.wave.picture, ImageScale.W_AUTO_H_50)} alt={drop.wave.name} width={20} height={20} className="tw-size-5 tw-shrink-0 tw-rounded-full tw-object-cover tw-ring-1 tw-ring-white/10" />;
    $[36] = drop.wave.name;
    $[37] = drop.wave.picture;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== drop.wave.name) {
    t20 = <span className="tw-min-w-0 tw-break-words">{drop.wave.name}</span>;
    $[39] = drop.wave.name;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t18 || $[42] !== t19 || $[43] !== t20) {
    t21 = <div className="tw-col-span-2"><NowMintingStatsItem label="Wave" allowWrap={true} value={<Link href={t18} className="tw-inline-flex tw-items-center tw-gap-2 tw-text-iron-100 tw-no-underline desktop-hover:hover:tw-text-iron-200">{t19}{t20}</Link>} /></div>;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = submittedAt ?? "\u2014";
  let t23;
  if ($[45] !== t22) {
    t23 = <NowMintingStatsItem label="Submitted" value={t22} />;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== drop.rating) {
    t24 = formatNumberWithCommas(drop.rating);
    $[47] = drop.rating;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = `${t24} TDH`;
  let t26;
  if ($[49] !== t25) {
    t26 = <NowMintingStatsItem label="Rating" value={t25} />;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t21 || $[52] !== t23 || $[53] !== t26) {
    t27 = <div className="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-4">{t21}{t23}{t26}</div>;
    $[51] = t21;
    $[52] = t23;
    $[53] = t26;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t17 || $[56] !== t27) {
    t28 = <div className="tw-p-5 md:tw-p-6 lg:tw-col-span-6 xl:tw-col-span-4"><div className="tw-flex tw-flex-col tw-gap-5">{t17}{t27}</div></div>;
    $[55] = t17;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t28 || $[59] !== t5) {
    t29 = <section className="tw-relative tw-z-50 tw-px-4 tw-pb-4 tw-pt-6 md:tw-px-6 md:tw-pb-8 md:tw-pt-10 lg:tw-px-8">{t4}<div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.03] tw-bg-iron-950"><div className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-x-6 tw-gap-y-6 lg:tw-grid-cols-12 xl:tw-grid-cols-9">{t5}{t28}</div></div></section>;
    $[58] = t28;
    $[59] = t5;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  return t29;
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}